import React, { useEffect, useRef, FC } from 'react';
import { INFINITE_SCROLL_THRESHOLD } from './HBNewTable.types';

interface Props {
    element: FC;
    hasMore?: boolean;
    loadMore: (page: number) => Promise<void>;
    threshold?: number;
    pageStart?: number;
    loader: React.ReactNode;
    children?: React.ReactNode;
    columnCount: number;
}

const HBNewTableInfiniteScroll = ({
    element: Element,
    hasMore,
    loadMore,
    threshold = INFINITE_SCROLL_THRESHOLD,
    pageStart = 1,
    loader,
    children,
    columnCount
}: Props) => {
    const isFetchingRef = useRef(false);

    const currentPageRef = useRef(pageStart);
    const observer = useRef<IntersectionObserver | null>(null);

    useEffect(() => {
        observer.current = new IntersectionObserver(
            async entries => {
                const firstEntry = entries[0];
                if (
                    firstEntry.isIntersecting &&
                    hasMore &&
                    !isFetchingRef.current
                ) {
                    isFetchingRef.current = true;
                    currentPageRef.current += 1;
                    await loadMore(currentPageRef.current);
                    isFetchingRef.current = false;
                }
            },
            { rootMargin: `0px 0px ${threshold}px 0px` }
        );

        const currentObserver = observer.current;
        const bottomRefCurrent = bottomRef.current;

        if (bottomRefCurrent && currentObserver) {
            currentObserver.observe(bottomRefCurrent);
        }

        return () => {
            if (bottomRefCurrent && currentObserver) {
                currentObserver.unobserve(bottomRefCurrent);
            }
        };
    }, [hasMore, loadMore, threshold]);

    const bottomRef = useRef<HTMLTableCellElement | null>(null);

    return (
        <Element>
            {children}
            {hasMore && (
                <td ref={bottomRef} colSpan={columnCount}>
                    {loader}
                </td>
            )}
        </Element>
    );
};

export default HBNewTableInfiniteScroll;
